import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api from "../lib/api";
import { ASSETS, SITE } from "../lib/constants";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("lotus_admin_token")) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", form);
      localStorage.setItem("lotus_admin_token", data.access_token);
      localStorage.setItem("lotus_admin_email", data.email);
      toast.success("Welcome back!");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-teal-50/40 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-slate-300/40 p-8 lg:p-10">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 flex items-center justify-center">
              <img src={ASSETS.logo} alt="logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-semibold text-slate-900">{SITE.name}</div>
              <div className="text-xs uppercase tracking-wider text-teal-600 font-medium">Admin Panel</div>
            </div>
          </div>

          <h1 className="mt-8 font-heading text-2xl font-medium text-slate-900">Sign in to dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Manage appointments, leads & contacts.</p>

          <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <Label className="text-xs font-medium text-slate-700">Email</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@example.com"
                className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                data-testid="admin-email"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Password</Label>
              <Input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-teal-500"
                data-testid="admin-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" /> Sign in
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
